import {
  DateFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { AgpmRepository } from '../../../../store/agpm-state/agpm-state.repository';
import { FileSelectEvent, FileRemoveEvent } from 'primeng/fileupload';
import { ResponseDoc } from '@/types';
import { map, Observable, of } from 'rxjs';

interface getAgpmInspectFormObjectProps {
  store: AgpmRepository;
  acOptions: SelectOption[];
  download?: (file: FileRepresentation) => void;
  openFile?: (file: FileRepresentation) => void;
  acDisabled?: Observable<boolean>;
}

export const getAgpmInspectFormObject = ({ store, acOptions, download, openFile, acDisabled }: getAgpmInspectFormObjectProps): DynamicForm => {
  const isDisabled = of(true);
  const refField: DynamicField<TextFieldProps> = {
    label: 'agpm.form.ref.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dto', 'ref'),
      onChange: (value: string) => {
        store.setNested('dto', 'ref', value);
      },
    },
  };
  const nomField: DynamicField<TextFieldProps> = {
    label: 'agpm.form.nom.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'nom'),
    isDisabled,
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        store.setNested('dto', 'nom', value);
      },
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'agpm.form.year.label',
    variant: FieldVariant.DATE,
    error: store.getNestedObservable<string>('errors', 'annee'),
    isRequired: true,
    isDisabled: acDisabled ?? isDisabled,
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: (value: Date) => {
        store.setNested('dto', 'annee', value.getFullYear().toString());
      },
      view: 'year',
      format: 'yy',
      minDate: new Date(new Date().getFullYear(), 0, 1),
      lastDate: new Date(new Date().getFullYear() + 1, 11, 31),
    },
  };

  const contenuField: DynamicField<EditorFieldProps> = {
    label: 'agpm.form.contenu.label',
    variant: FieldVariant.EDITOR,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'contenu'),
    isDisabled,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'contenu'),
      onChange: (value: string) => {
        store.setNested('dto', 'contenu', value);
      },
    },
  };
  const uploadField: DynamicField<FileFieldProps> = {
    label: 'agpm.form.files.label',
    variant: FieldVariant.FILE,
    isDisabled,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      onFileSelect(event: FileSelectEvent) {
        store.setNested('dto', 'files', event.currentFiles);
      },
      onFileRemoved(event: FileRemoveEvent) {
        store.setNested(
          'dto',
          'files',
          store.getNested<File[]>('dto', 'files').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('response', 'docs')
        .pipe(
          map(
            docs =>
              docs &&
              docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),

      onFileRepresentationChange: (file: FileRepresentation) => {
        store.setNested(
          'response',
          'docs',
          store.getNested<ResponseDoc[]>('response', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'dto',
          'docIds',
          store.getNested<number[]>('dto', 'docIds').filter(id => id !== file.id)
        );
      },
      openFile: (file: FileRepresentation) => openFile?.(file),
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      onAllRemoved: () => {
        store.setNested('response', 'docs', []);
        store.setNested('dto', 'docIds', []);
      },
    },
  };

  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'agpm.form.acOptions.title',
    variant: FieldVariant.SELECT,
    isDisabled: acDisabled ?? isDisabled,
    isRequired: true,
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code);
      },
      placeholder: 'agpm.form.acOptions.placeholder',
      options: acOptions,
      filterEnabled: true,
    },
  };
  const remarqueField = {
    label: 'agpm.form.remarque.label',
    variant: FieldVariant.EDITOR,
    isRequired: false,
    isDisabled,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'remarque'),
      onChange: (value: string) => {
        store.setNested('dto', 'remarque', value);
      },
    },
  };

  return {
    title: 'Avis général de passation de marché',
    description: 'cette page pernet de modifier un  avis en corrigez les informations existantes',
    isHeaderHidden: true,
    showLegend: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          { fields: [yearField, acOptionsField] },
          {
            fields: [refField, nomField],
          },
          {
            fields: [contenuField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [remarqueField],
          },
        ],
      },
    ],
  };
};
